import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import SVG from 'components/base/SVG';
import Button, { SolidButton } from 'components/Button';

import css from './CustomFilterModal.scss';


// Same caution copy as the legacy SearchFilterViewer (app/components/SearchFilter/Viewer.js).
export const warningMessage = 'Please note that changing your list filters will alter your alerts, settings and additional criteria set throughout this list. The lists are set up for complete automation so changing and adjusting this list could result in loss of property listings.';

export const CautionBanner = () => (
  <div className={css.banner}>
    <SVG icon="filter-warning-banner" className={css.bannerIcon} />
    <span>{warningMessage}</span>
  </div>
);

export const CautionOverlay = ({ editEnabled, onCancel, onEdit, loading }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    // The panel behind the caution overlay is inert until the user chooses
    // Cancel/Edit. The overlay covers pointer input, but keyboard events (typing,
    // Tab, arrows, Enter, app hotkeys) would still reach focused controls behind
    // it. Swallow every key event at the capture phase unless it originates inside
    // the overlay itself, so nothing behind can be operated by keyboard.
    //
    // Escape is the one allowed key: it cancels the overlay (same as clicking
    // Cancel). We handle it ourselves and still stop propagation so the modal's
    // own Escape-to-close doesn't also fire; onCancel only runs on keydown so it
    // isn't triggered again by the paired keyup.
    const blockKeys = (event) => {
      if (event.key === 'Escape') {
        if (event.type === 'keydown') onCancel();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (overlayRef.current && overlayRef.current.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const events = ['keydown', 'keypress', 'keyup'];
    events.forEach(name => document.addEventListener(name, blockKeys, true));

    // Move focus off whatever was focused behind the overlay so Tab/typing don't
    // resume there; keyboard focus now starts inside the overlay.
    if (overlayRef.current) overlayRef.current.focus();

    return () => events.forEach(name => document.removeEventListener(name, blockKeys, true));
  }, [onCancel]);

  return (
    <div className={css.overlay} ref={overlayRef} tabIndex={-1}>
      <div className={css.overlayContent}>
        <SVG icon="filter-warning-overlay" className={css.iconWarning} />
        <div className={css.warningText}>{warningMessage}</div>
        <div className={css.overlayButtons}>
          <button loading={loading} onClick={onCancel} className={css.btnSecondary}>Cancel</button>
          {!editEnabled ? null : <button loading={loading} onClick={onEdit} className={css.btnPrimary}>Edit List</button>}
        </div>
      </div>
    </div>
  );
};

CautionOverlay.propTypes = {
  editEnabled: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

CautionOverlay.defaultProps = {
  editEnabled: false,
  loading: false,
};
